import { Capacitor } from '@capacitor/core';
import type { DeviceInfo } from '@capacitor/device';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import type { AppInfo } from "@capacitor/app/dist/esm/definitions";

export const platform = Capacitor.getPlatform();
export const isAndroid = platform === 'android';
export const isIos = platform === 'ios';
export const isNativePlatform = Capacitor.isNativePlatform();
export const isWebPlatform = !isNativePlatform;
export const appEnv = import.meta.env.VITE_APP_ENV;
export const viteMode = import.meta.env.MODE;
export const isLocalEnv = import.meta.env.VITE_APP_ENV === 'local';
export const isStagingEnv = import.meta.env.VITE_APP_ENV === 'staging';
export const isProductionEnv = import.meta.env.VITE_APP_ENV === 'production';
export const isDevelopmentBuild = import.meta.env.DEV;
export const isProductionBuild = import.meta.env.PROD;
export const appUrl = import.meta.env.VITE_APP_URL;
export const commit = import.meta.env.COMMIT_SHA;

let appInfos: null|AppInfo = null;
let deviceInfos: null|DeviceInfo = null;

export async function getAppInfos(): Promise<AppInfo> {
  if (!appInfos) {
    if (isNativePlatform) {
      appInfos = await App.getInfo();
    } else {
      appInfos = {
        version: import.meta.env.VITE_APP_VERSION,
        build: '0',
        id: '0',
        name: 'Finanzmanager',
      }
    }
  }

  return appInfos;
}

export async function getDeviceInfos(): Promise<DeviceInfo> {
  if (!deviceInfos) {
    deviceInfos = await Device.getInfo();
  }

  return deviceInfos;
}

// from https://gist.github.com/lyquix-owner/34a3ed98b990c3c61151d30de2f42702
export function getBrowser() {
  const ua = navigator.userAgent;
  let browser = '';

  function getFirstMatch(regex: RegExp) {
    const match = ua.match(regex);
    return (match && match.length > 1 && match[1]) || '';
  }

  function getSecondMatch(regex: RegExp) {
    const match = ua.match(regex);
    return (match && match.length > 1 && match[2]) || '';
  }

  if (/opera|opr/i.test(ua)) {

    const v = getFirstMatch(/version\/(\d+(\.\d+)?)/i) || getFirstMatch(/(?:opera|opr)[\s\/](\d+(\.\d+)?)/i);
    browser = `Opera ${v}`
  }  else if (/msie|trident/i.test(ua)) {
    const v = getFirstMatch(/(?:msie |rv:)(\d+(\.\d+)?)/i);
    browser = `Internet Explorer ${v}`;
  } else if (/chrome.+? edge/i.test(ua)) {
    const v = getFirstMatch(/edge\/(\d+(\.\d+)?)/i);
    browser = `Microsft Edge ${v}`;
  } else if (/chrome|crios|crmo/i.test(ua)) {
    const v = getFirstMatch(/(?:chrome|crios|crmo)\/(\d+(\.\d+)?)/i);
    browser = `Google Chrome ${v}`
  } else if (/firefox/i.test(ua)) {

    const v = getFirstMatch(/(?:firefox)[ \/](\d+(\.\d+)?)/i);
    browser = `Firefox ${v}`
  } else if (!(/like android/i.test(ua)) && /android/i.test(ua)) {
    const v = getFirstMatch(/version\/(\d+(\.\d+)?)/i);
    browser = `Android ${v}`
  } else if (/safari/i.test(ua)) {
    const v = getFirstMatch(/version\/(\d+(\.\d+)?)/i);
    browser = `Safari ${v}`
  } else {
    const v = getSecondMatch(/^(.*)\/(.*) /);
    browser = `${getFirstMatch(/^(.*)\/(.*) /)} ${v}`
  }

  return browser;
}

export async function getDeviceName() {
  const deviceInfos = await getDeviceInfos();

  if (deviceInfos.name) return deviceInfos.name;

  return isNativePlatform ? 'Native Mobile Device' : getBrowser();
}

export async function getDeviceId() {
  const id = await Device.getId();

  if (id.identifier) return id.identifier;

  return null;
}

export async function getVersionString() {
  const { build, version } = await getAppInfos();
  return isNativePlatform ? `${version} (${build})` : version;
}

